import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Clock, XCircle, LogOut } from "lucide-react";

export const PendingPage = () => {
    const { user, logout } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "SHOP_OWNER" || user.status !== "PENDING_APPROVAL") {
        return <Navigate to="/dashboard" replace />;
    }
    return (
        <StatusFrame
            icon={<Clock size={30} />}
            title="Awaiting verification"
            subtitle={
                <>
                    Your application for <b>{user.business_name}</b> is under
                    review. Our team gets back within{" "}
                    <b>24 hours</b>.
                </>
            }
            onLogout={logout}
            testId="pending-page"
        />
    );
};

export const RejectedPage = () => {
    const { user, logout } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "SHOP_OWNER" || user.status !== "REJECTED") {
        return <Navigate to="/dashboard" replace />;
    }
    return (
        <StatusFrame
            icon={<XCircle size={30} />}
            iconClass="bg-rose-500"
            title="Application rejected"
            subtitle={
                <>
                    Unfortunately, we couldn't approve{" "}
                    <b>{user.business_name || "your business"}</b>.
                    {user.rejection_reason ? (
                        <div className="mt-3 rounded-lg bg-rose-50 p-3 text-left text-sm text-rose-800">
                            <div className="mb-1 font-semibold">Reason from our team:</div>
                            <div>{user.rejection_reason}</div>
                        </div>
                    ) : null}
                </>
            }
            onLogout={logout}
            testId="rejected-page"
        />
    );
};

const StatusFrame = ({ icon, iconClass, title, subtitle, onLogout, testId }) => (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4" data-testid={testId}>
        <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center">
            <div className="mx-auto mb-4 flex justify-center">
                <Logo size={36} />
            </div>
            <div
                className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full text-white nest-shadow ${iconClass || "nest-gradient"}`}
            >
                {icon}
            </div>
            <h1 className="mt-6 text-2xl font-bold text-neutral-900">{title}</h1>
            <div className="mt-2 text-sm text-neutral-600">{subtitle}</div>
            <Button
                variant="outline"
                onClick={onLogout}
                className="mt-6 w-full"
                data-testid="status-logout-btn"
            >
                <LogOut size={16} className="mr-2" /> Sign out
            </Button>
        </div>
    </div>
);
